/**
 * Map key (MapDef.legend): a small, always-open key at a corner of the drawn map
 * that explains the board's rule symbols — e.g. Royal Rumble's orange
 * 5 v 6 crossings, red 8 v 6 chevrons and blue one-way arrows. Optional per map.
 * Symbol colours come from mapSymbols.ts, the same source the routes and
 * chevrons draw with. The map editor shows the same key (tools/map-editor/app.js).
 */
import React, { useState } from 'react'
import { Text, View } from 'react-native'
import Svg, { Line, Polygon, Polyline } from 'react-native-svg'
import type { MapLegend as MapLegendDef, MapLegendItem } from '@warcarp/api-types'
import { MAP_SYMBOL_COLORS, mapSymbolColor } from './mapSymbols'
import type { MapContentInsets } from './mapLegendLayout'

const corner = (position: MapLegendDef['position'], insets: MapContentInsets) => ({
  'top-left': { top: insets.top, left: insets.left },
  'top-right': { top: insets.top, right: insets.right },
  'bottom-left': { bottom: insets.bottom, left: insets.left },
  'bottom-right': { bottom: insets.bottom, right: insets.right },
}[position ?? 'bottom-right'])

function Swatch({ item, routeHalo, theme, scale = 1 }: { item: MapLegendItem; routeHalo: string; theme: 'light' | 'dark'; scale?: number }) {
  const color = mapSymbolColor(item.kind ?? (item.symbol === 'chevron' ? 'advantage' : 'route'), theme)
  if (item.symbol === 'chevron') {
    return (
      <Svg width={26 * scale} height={16 * scale} viewBox="0 0 26 16">
        <Polyline points="9,3 16,8 9,13" fill="none" stroke={MAP_SYMBOL_COLORS.chevronHalo} strokeWidth={5} strokeLinecap="round" strokeLinejoin="round" />
        <Polyline points="9,3 16,8 9,13" fill="none" stroke={color} strokeWidth={2.8} strokeLinecap="round" strokeLinejoin="round" />
      </Svg>
    )
  }
  return (
    <Svg width={26 * scale} height={16 * scale} viewBox="0 0 26 16">
      <Line x1={2} y1={8} x2={item.symbol === 'arrow' ? 17 : 24} y2={8} stroke={routeHalo} strokeWidth={4.2} strokeLinecap="round" />
      <Line x1={2} y1={8} x2={item.symbol === 'arrow' ? 17 : 24} y2={8} stroke={color} strokeWidth={2.6} strokeLinecap="round" />
      {item.symbol === 'arrow' && <Polygon points="25,8 16,3 16,13" fill={color} stroke={routeHalo} strokeWidth={0.9} strokeLinejoin="round" />}
    </Svg>
  )
}

export function MapLegend({ legend, theme, insets, centerTop, compact = false, darkSeaRoutes = false }: {
  legend: MapLegendDef; theme: 'light' | 'dark'; insets: MapContentInsets
  /** MapDef.legend.centerY in wrapper pixels: centre the key there instead of using the corner's top/bottom. */
  centerTop?: number
  /** A smaller key with tighter padding, for a map drawn small (phones). */
  compact?: boolean
  darkSeaRoutes?: boolean
}) {
  const [height, setHeight] = useState<number | null>(null)
  const dark = theme === 'dark'
  const ink = dark ? '#e6e1c4' : '#18170f'
  const card = dark ? 'rgba(10, 22, 16, 0.88)' : 'rgba(244, 236, 211, 0.92)'
  const halo = dark ? '#0a1610' : '#fffbeb'
  return (
    <View
      pointerEvents="none"
      onLayout={centerTop === undefined ? undefined : (e) => setHeight(e.nativeEvent.layout.height)}
      style={{
        position: 'absolute',
        ...(centerTop === undefined
          ? corner(legend.position, insets)
          : {
              // Vertically centred on centerTop; hidden for the first layout pass until its height is known.
              top: centerTop - (height ?? 0) / 2,
              opacity: height === null ? 0 : 1,
              ...((legend.position ?? 'bottom-right').endsWith('left') ? { left: insets.left } : { right: insets.right }),
            }),
      }}
    >
      <View
        testID="map-legend"
        accessible
        accessibilityLabel={`Map key: ${legend.items.map((item) => item.label).join(', ')}`}
        style={{ backgroundColor: card, borderRadius: compact ? 7 : 8, paddingHorizontal: compact ? 7 : 9, paddingVertical: compact ? 4.5 : 6, borderWidth: 1, borderColor: dark ? '#3c4630' : '#d8cfb0' }}
      >
        <View style={{ gap: compact ? 3 : 4 }}>
          {legend.items.map((item) => (
            <View key={`${item.symbol}-${item.label}`} style={{ flexDirection: 'row', alignItems: 'center', gap: compact ? 4.5 : 6 }}>
              <Swatch item={item} theme={theme} routeHalo={darkSeaRoutes ? '#0a1610' : halo} scale={compact ? 0.875 : 1} />
              <Text style={{ color: ink, fontSize: compact ? 11 : 12 }}>{item.label}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  )
}
